import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import { theme } from '@Guidelines/Theme/theme';
import buildDependencyOptions, {
	resolveSectionTitle,
} from '@UI/Pages/Administration/DocumentGroups/DocumentGroupEdit/DocumentSettings/Sections/SplitSection/Helpers/buildDependencyOptions';
import type DocumentSettingsFormData from '@UI/Pages/Administration/DocumentGroups/DocumentGroupEdit/FormData/DocumentSettingsFormData';
import PillChoice from '@UI/Pages/Administration/DocumentGroups/DocumentGroupEdit/Shared/PillChoice';
import type DocumentTypeSectionView from '@UI/Store/OrganizationWorkflows/Views/DocumentTypeSectionView';
import { css } from '@emotion/react';
import * as React from 'react';
import { Button } from '@verclam/ui-kit';

const rowStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.625rem;
	padding: 0.75rem 0;
	border-bottom: 1px solid ${theme.colors.border};
`;

const lineStyle = css`
	display: grid;
	grid-template-columns: minmax(0, 1fr) minmax(0, 1.4fr) auto;
	gap: 0.75rem;
	align-items: center;

	@media (max-width: ${theme.layout.compactBreakpoint}) {
		grid-template-columns: minmax(0, 1fr) auto;
	}
`;

const nameStyle = css`
	font-family: ${theme.fonts.body};
	font-size: 0.84375rem;
	font-weight: ${theme.fontWeights.semibold};
	line-height: 1.4;
	color: ${theme.colors.ink};
`;

const afterStyle = css`
	font-family: ${theme.fonts.body};
	font-size: 0.8125rem;
	line-height: 1.45;
	color: ${theme.colors.inkSecondary};

	@media (max-width: ${theme.layout.compactBreakpoint}) {
		grid-column: 1 / -1;
		grid-row: 2;
	}
`;

type Props = {
	readonly section: DocumentTypeSectionView;
	readonly sections: readonly DocumentTypeSectionView[];
	readonly settings: DocumentSettingsFormData;
	/** Whether the choice of what this section waits for is open. */
	readonly isEdited: boolean;
	readonly onEditedPathChange: (path: string | null) => void;
	readonly onChange: React.Dispatch<React.SetStateAction<DocumentSettingsFormData>>;
};

/** One section of the template, what it is written after, and the way to change it. */
const SectionDependencyRow: React.FC<Props> = ({
	section,
	sections,
	settings,
	isEdited,
	onEditedPathChange,
	onChange,
}) => {
	const options = React.useMemo(
		() => buildDependencyOptions(sections, section.path),
		[sections, section.path],
	);

	const storedTargets = settings.sectionDependencies[section.path];
	const selectedOptions = React.useMemo(
		() => options.filter((option) => storedTargets?.includes(option.value) === true),
		[options, storedTargets],
	);
	const targets = React.useMemo(() => selectedOptions.map((option) => option.value), [selectedOptions]);
	const afterText =
		selectedOptions.length > 0
			? selectedOptions.map((option) => option.label).join(', ')
			: 'Aucune : rédigée dès le départ';

	const handleChange = React.useCallback(
		(values: readonly string[]) => {
			onChange((current) => current.setSectionDependency(section.path, values));
		},
		[onChange, section.path],
	);

	const handleEditClick = React.useCallback(() => {
		onEditedPathChange(isEdited ? null : section.path);
	}, [isEdited, onEditedPathChange, section.path]);

	return (
		<div css={rowStyle}>
			<div css={lineStyle}>
				<span css={nameStyle}>{resolveSectionTitle(section)}</span>
				<span css={afterStyle}>{afterText}</span>
				<Button
					type="button"
					size="sm"
					severity="secondary"
					label={isEdited ? 'Fermer' : 'Modifier'}
					onClick={handleEditClick}
				/>
			</div>
			<ConditionalLoader data={isEdited}>
				<PillChoice
					multiple
					name={`dependencies-${section.path || 'general'}`}
					label="Rédigée après"
					isLabelHidden
					options={options}
					value={targets}
					onChange={handleChange}
				/>
			</ConditionalLoader>
		</div>
	);
};

export default SectionDependencyRow;
